import { platformModifierKeyOnly } from 'ol/events/condition';
import { LayerProperty } from 'cadenza/map/layer-property';
import { MapProperty } from 'cadenza/map/map-property';
import { getSelectedLayerFeatures, MapsApi } from 'cadenza/api-client/maps-api';
import { logger } from 'cadenza/utils/logging';
import {
  createFeatureSelectionData,
  hasAttributeTable,
  refreshLayer
} from 'cadenza/map/layer-utils';
import { context } from 'cadenza/map/context';
import { MAP } from 'cadenza/map-dashboard/event-types';
import { isVectorLayer } from 'cadenza/map/vector-layer/vector-layer';

export class SelectBase {

  #isMapCondition;
  #isPolygonCondition;

  constructor (map, { refreshSelectedLayerOnly = true, selectionUrl, isMapCondition, isPolygonCondition } = {}) {
    this._map = map;
    this._mapTargetElement = map.getTargetElement();
    this._refreshSelectedLayerOnly = refreshSelectedLayerOnly;
    this.#isMapCondition = isMapCondition;
    this.#isPolygonCondition = isPolygonCondition;
    this._mapApi = new MapsApi({ selectionUrl, mapId: map.get(MapProperty.ID) });
  }

  selectFeatures (originalEvent, feature) {
    this._selectedLayer = this._getSelectedLayer() || this._getFirstVisibleFeatureLayer();
    if (this._selectedLayer && this._selectedLayer.get(LayerProperty.HAS_FEATURES)) {
      const srsName = this._map.getView().getProjection().getCode();
      const selectedLayerId = this._selectedLayer.get(LayerProperty.ID);
      const geometry = feature.getGeometry();
      const mapSize = this._map.getSize();
      const mapExtent = this._map.getView().calculateFullExtent();
      const modifiers = {
        ctrlKey: platformModifierKeyOnly({ originalEvent })
      };
      const mapScale = context(this._map).mapScale;
      return this._mapApi.selectFeatures(selectedLayerId, geometry, mapSize, mapExtent, modifiers, mapScale)
        .then(selectedFeatures => {
          if (selectedFeatures) {
            this._selectedLayer.set(LayerProperty.SELECTED_FEATURE_IDS, selectedFeatures.selectedFeatureIDs);
            if (hasAttributeTable(this._selectedLayer)) {
              const detail = createFeatureSelectionData([ selectedLayerId ], selectedFeatures.selectedFeatureIDs);
              this._mapTargetElement.dispatchEvent(new CustomEvent(MAP.SELECTOR_LAYER_SELECTION_CHANGED, { detail }));
            }
            if (this.#isMapCondition) { // We're inside an iframe, so we can access the modal in which the iframe is shown
              const mapConditionModal = window.parent.document.querySelector('.d-map-condition-modal');
              mapConditionModal.setSelectedFeatureIds(selectedFeatures.selectedFeatureIDs);
            } else if (this.#isPolygonCondition) {
              return getSelectedLayerFeatures(selectedLayerId, srsName, mapScale);
            }
          }
          this._onSelectedFeaturesSuccessful();
        }).then(features => {
          if (features) {
            const polygonConditionModal = window.parent.document.querySelector('.d-polygon-condition-modal');
            const allGeometries = features.features.map(feature => ({
              geometry: JSON.stringify(feature.geometry),
              srs: srsName
            }));
            polygonConditionModal.setFeatureGeometries(allGeometries);
            this._onSelectedFeaturesSuccessful();
          }
        })
        .catch(err => { logger.error('Cannot select features', err); });
    } else {
      logger.info('Could not find any visible layer with features. Request not dispatched, it would have done nothing.');
      return Promise.resolve();
    }
  }

  hasContextMenu () {
    const flag = this._map.get(MapProperty.CONTEXT_MENU_ENABLED);
    const layer = this._getSelectedLayer();
    const features = layer && layer.get(LayerProperty.HAS_FEATURES);
    return flag && features;
  }

  _getSelectedLayer () {
    return this._map.get(MapProperty.SELECTED_LAYER);
  }

  _getFirstVisibleFeatureLayer () {
    return this._map.getLayers().getArray().find(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST)
      && layer.get(LayerProperty.HAS_FEATURES));
  }

  _getLayerByName () {
    return this._map.getLayers().getArray().find(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST)
      && layer.get(LayerProperty.HAS_FEATURES));
  }

  _onSelectedFeaturesSuccessful () {
    if (this._refreshSelectedLayerOnly) {
      refreshLayerIfNotVectorLayer(this._selectedLayer);
    } else {
      this._map.getLayers().forEach(refreshLayerIfNotVectorLayer);
    }
  }

}

// Vector layers manage selection on the client, so no need to refresh them on selection change.
function refreshLayerIfNotVectorLayer (layer) {
  if (!isVectorLayer(layer)) {
    refreshLayer(layer);
  }
}
